import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import {EntitySummary} from 'constants/UserConstants';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllBoxSchemasWithRegistryQuery} from 'inventory/__generated/AllBoxSchemasWithRegistryQuery';
import {AllContainerSchemasWithRegistryQuery} from 'inventory/__generated/AllContainerSchemasWithRegistryQuery';
import {AllLocationSchemasWithRegistryQuery} from 'inventory/__generated/AllLocationSchemasWithRegistryQuery';
import {AllPlateSchemasWithRegistryQuery} from 'inventory/__generated/AllPlateSchemasWithRegistryQuery';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  useBoxSchemasQueryWithRegistry,
  useContainerSchemasQueryWithRegistry,
  useLocationSchemasQueryWithRegistry,
  usePlateSchemasQueryWithRegistry,
} from 'inventory/useStorableSchemasQuery';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {ProcessStatusLifecyclesQuery} from 'processes/settings/__generated/ProcessStatusLifecyclesQuery';
import {
  ProcessTaskSchemaEditorQuery,
  ProcessTaskSchemaEditorQueryVariables,
} from 'processes/settings/__generated/ProcessTaskSchemaEditorQuery';
import CloneProcessTaskSchemaModal from 'processes/settings/CloneProcessTaskSchemaModal';
import ConfigureTaskFinderSection from 'processes/settings/ConfigureTaskFinderSection';
import CanOnlyBePartOfFlowchartField from 'processes/settings/fields/CanOnlyBePartOfFlowchartField';
import IsPropagateWatchersEnabledField from 'processes/settings/fields/IsPropagateWatchersEnabledField';
import ResponsiblePartyLookupConfigField from 'processes/settings/fields/ResponsiblePartyLookupConfigField';
import StatusLifecycleField from 'processes/settings/fields/StatusLifecycleField';
import GraphBuilderFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/GraphBuilderFlowchartConfigEditor';
import {ProcessFlowchartConfigEditorHeaderActionsProps} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorHeaderTypes';
import ProcessSchemaFieldTableRow from 'processes/settings/ProcessSchemaFieldTableRow';
import {
  DefaultCreationFolderSelectorContainer,
  DefaultEntryExecutionFolderSelectorContainer,
} from 'processes/settings/ProcessSchemaFolderSelectors';
import ProcessSchemaMetadataSectionRow, {
  NameField,
  OwnerField,
  PrefixField,
} from 'processes/settings/ProcessSchemaMetadataSectionRow';
import ProcessTaskGroupInstructionsField from 'processes/settings/ProcessTaskGroupInstructionsField';
import {
  createOwnerToRegistryMap,
  getRegistryIdForOwner,
} from 'processes/settings/ProcessTaskSchemaEditorUtils';
import {
  cacheUpdatedTaskSchema,
  createOrUpdateSnapshotField,
  insertOrReplaceTaskSchema,
  isEmptyOrWhitespace,
  UpdateProcessSchema,
  updateProcessTaskSchema,
  updateProcessTaskSchemaForTaskDefinitionVersion,
} from 'processes/settings/ProcessTaskSchemaUtils';
import RemoveOutputSchemaModal from 'processes/settings/RemoveOutputSchemaModal';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {isExecutionBlockedByTemplatePublishing} from 'processes/util/ProcessTaskExecutionUtil';
import FieldTable, {
  ClientSchemaField,
  FieldNameRenderingProps,
  FieldTableLoading,
} from 'registry/field/FieldTable';
import {getNewOrUpdatedSnapshotFields, haveFieldsChanged} from 'registry/field/FieldUtil';
import {AllResultSchemasQuery} from 'results/__generated/AllResultSchemasQuery';
import {useAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {
  filterSchemasByOrganizationJson,
  filterSchemasByRegistryOrganization,
} from 'schemas/util/FilterSchemasByOrganization';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import SettingsPageContainer, {SettingsPageContainerProps} from 'settings/SettingsPageContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PartiesField from 'ui-library/inputs/PartiesField';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import enumIncludes from 'util/enumIncludes';
import {withLoadingScreen} from 'util/Promises';
import strUtil from 'util/strUtil';
import QueryingValidationRulesTable from 'validation_rules/components/ValidationRulesTable';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  MinimalProcessTaskSchemaEditorQuery,
  MinimalProcessTaskSchemaEditorQueryVariables,
} from './__generated/MinimalProcessTaskSchemaEditorQuery';
import {EntryTemplateForConfigureButton, isEntryTemplatePreCreate} from './ConfigureExecutionTemplateButton';
import ConfigureExecutionTemplateSection from './ConfigureExecutionTemplateSection';
import AutopopulateFromTasksField from './fields/AutopopulateFromTasksField';
import CanSetAssigneeOnTaskCreationField from './fields/CanSetAssigneeOnTaskCreationField';
import ExecutionTypeField from './fields/ExecutionTypeField';
import ProcessFlowchartConfigEditor from './graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditor';
import {ProcessTaskDefinitionVersionGraphQL} from './graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import ProcessTaskSchemaFormsList from './ProcessTaskSchemaFormsList';
import ProcessTaskSchemaRequestV2DefinitionsList from './ProcessTaskSchemaRequestV2DefinitionsList';
import {ProcessTaskSchema} from './ProcessTaskSchemaUtils';

export type MinimalProcessTaskSchema = ExtractByTypename<
  MinimalProcessTaskSchemaEditorQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

type ProcessOutputSchema = NonNullable<MinimalProcessTaskSchema['outputSchema']>;

type FormDefinitionNode = ExtractByTypename<
  MinimalProcessTaskSchema['relevantForms'][number],
  'FormDefinitionNode'
>;

type ProcessResponsibleParty = MinimalProcessTaskSchema['responsibleParties'][number];

type ProcessResponsiblePartyLookupConfig = MinimalProcessTaskSchema['responsiblePartyLookupConfigs'][number];

type TagSchemaGQL = TagSchemasWithRegistryQuery['tagSchemas'][number];
type ResultSchemasGQL = AllResultSchemasQuery['resultSchemas'][number];
type ProcessTaskStatusLifecycle = ProcessStatusLifecyclesQuery['processTaskStatusLifecycles'][number];
type PlateSchemaGQL = AllPlateSchemasWithRegistryQuery['plateSchemas'][number];
type LocationSchemaGQL = AllLocationSchemasWithRegistryQuery['locationSchemas'][number];
type ContainerSchemaGQL = AllContainerSchemasWithRegistryQuery['containerSchemas'][number];
type BoxSchemaGQL = AllBoxSchemasWithRegistryQuery['boxSchemas'][number];
type ProcessTaskSchemaGQL =
  AllProcessTaskSchemasForSchemaConfigQuery['processTaskSchemasSupportingPlatformFeature'][number];

const SAVING_TASK_SCHEMA_MESSAGE = 'Saving...';
const CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE =
  "Duplicating task schemas of 'Flowchart' execution type isn't supported at this time. You can duplicate task schemas of 'Direct' or 'Entry' execution type.";

/**
 * Creates an array of overflow menu options for process task schema header actions.
 * This is a shared function used by both the header and various editors.
 */
function createProcessTaskSchemaHeaderActionsOptions(
  processTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
): Array<OverflowMenuOption> {
  const isCloningDisabled = processTaskSchema.executionType === ProcessExecutionType.FLOWCHART;

  return [
    {
      icon: 'copy',
      contents: <span>Duplicate</span>,
      onSelect: () => {
        runModal(CloneProcessTaskSchemaModal, {
          originalProcessTaskSchema: processTaskSchema,
          actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
          actionMethod: ActionMethod.BUTTON_CLICK,
        });
      },
      isDisabled: isCloningDisabled,
      tooltipTitle: isCloningDisabled ? CLONE_TASK_SCHEMA_DISABLED_TOOLTIP_MESSAGE : null,
      dataTestAttrs: {component: 'ProcessTaskSchemaEditor', element: 'cloneTaskSchemaButton'},
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        AuditActions.exportAuditLogs([processTaskSchema.id]);
      },
      dataTestAttrs: {component: 'SchemaSettingsExportDropdown', element: 'exportAuditLogButton'},
    },
  ];
}

export const PROCESS_TASK_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query ProcessTaskSchemaEditorQuery($schemaId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        id
        flowchartConfig {
          ...ProcessFlowchartConfigGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const STATUS_LIFECYCLES_QUERY: DocumentNode = gql`
  query ProcessStatusLifecyclesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskStatusLifecycles {
      ... on ProcessTaskStatusLifecycleNode {
        id
        name
        schemaExecutionType
      }
    }
  }
`;

type StorableSchemaGQL = BoxSchemaGQL | ContainerSchemaGQL | LocationSchemaGQL | PlateSchemaGQL;

export const DEFAULT_TASK_SCHEMA_PREFIX = 'T';
export const DEFAULT_OUTPUT_SCHEMA_PREFIX = 'O';

// Keep in sync with benchling/processes/api/process_task_schemas.py EXECUTION_TYPES_WITH_TASK_FINDER
export const EXECUTION_TYPES_WITH_TASK_FINDER = new Set([
  ProcessExecutionType.DIRECT,
  ProcessExecutionType.ENTRY,
]);

export enum ProcessTaskSchemaTabs {
  CONFIGURATION = 'configuration',
  FORMS = 'forms',
  VALIDATION_RULES = 'validation_rules',
  REQUEST_V2_DEFINITIONS = 'request_v2_definitions',
  SYSTEM_NAMES = 'system_names',
}

interface BaseProcessTaskSchemaEditorContainerProps {
  subtitle: string | null;
  processTaskSchema?: MinimalProcessTaskSchema;
}

interface ProcessTaskSchemaEditorContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  children: React.ReactNode;
}

/** The same as ProcessTaskSchemaEditorContainerProps except it expects multiple children for each of the page's tabs */
interface ProcessTaskSchemaEditorTabbedContainerProps extends BaseProcessTaskSchemaEditorContainerProps {
  schemaId: string;
  configurationTab: React.ReactNode;
  systemNamesTab?: React.ReactNode;
  tabContentClassName?: string;
  tabNavClassName?: string;
  tabPaneClassName?: string;
}

export enum ProcessTaskSchemaEditorContentSections {
  TASK = 'TASK',
  TASK_FIELDS = 'TASK_FIELDS',
  OUTPUT = 'OUTPUT',
  FLOWCHART = 'FLOWCHART',
}

interface ProcessTaskSchemaEditorContentProps {
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  tagSchemas: Array<TagSchemaGQL> | null;
  taskSchemas: Array<ProcessTaskSchemaGQL> | null;
  statusLifecycles: Array<ProcessTaskStatusLifecycle> | null;
  refetchProcessSchema?: () => void;
  afterTaskSchemaUpdate?: (
    newTaskSchema: ProcessTaskSchemaGraphQL,
    newTaskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL | null
  ) => void;
  resultSchemas: Array<ResultSchemasGQL> | null;
  isAnyPartOfFlowchartConfigInaccessible: boolean | null;
  // Hides the flowchart config section of the task schema editor,
  // specifically for when this component is used in the flowchart config editor
  sectionsToHide?: Array<ProcessTaskSchemaEditorContentSections>;
  locked?: boolean;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest) => Promise<void>;
  noSectionSpacing?: boolean;
  taskDefinitionId?: string;
  isSettingsPage?: boolean;
  registryId?: string;
  stickyCreateButton?: boolean;
}

interface ToggleOutputSchemaButtonProps {
  hasOutputSchema: boolean;
  onClick: () => void;
}

interface ProcessSchemaErrorListProps {
  error: string;
}

export interface ProcessSchemaFieldTableProps {
  hasBottomLink: boolean;
  schema: MinimalProcessTaskSchema | ProcessOutputSchema | null;
  ownerUrlId: string;
  ownerId: string;
  registryId?: string;
  fields: Array<ClientSchemaField>;
  onFieldsChange: (fields: Array<ClientSchemaField>) => void;
  schemaType: string;
  storableSchemas: Array<StorableSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  isDisabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
  changeWarnings?: Array<JSX.Element>;
  fieldNameRenderingProps?: FieldNameRenderingProps;
  compactMode?: boolean;
}

interface ProcessTaskSchemaEditorSectionProps {
  sectionTitle: string;
  sectionNumber: number;
  children?: React.ReactNode;
  isLastSection?: boolean;
  actionButton?: {
    onClick: () => void;
    tooltipTitle: string;
    icon: BenchlingIconName;
  };
  shouldHideSectionHeader: boolean;
  noSectionSpacing?: boolean;
}

interface InnerProcessTaskSchemaEditorPropsDataLoading {
  schemaId: string | null;
  storableSchemas: null;
  tagSchemas: null;
  taskSchemas: null;
  resultSchemas: null;
  statusLifecycles: null;
  isAnyPartOfFlowchartConfigInaccessible: null;
  registryId?: string;
}

interface InnerProcessTaskSchemaEditorPropsWithData {
  schemaId: string | null;
  storableSchemas: Array<StorableSchemaGQL>;
  tagSchemas: Array<TagSchemaGQL>;
  taskSchemas: Array<ProcessTaskSchemaGQL>;
  resultSchemas: Array<ResultSchemasGQL>;
  statusLifecycles: Array<ProcessTaskStatusLifecycle>;
  isAnyPartOfFlowchartConfigInaccessible: boolean;
  registryId?: string;
}

type InnerProcessTaskSchemaEditorProps =
  | InnerProcessTaskSchemaEditorPropsDataLoading
  | InnerProcessTaskSchemaEditorPropsWithData;

interface ProcessTaskSchemaEditorForCreateProps {
  schemaId: null;
  /**
   * The execution type of the schema that is being created.
   * When this is provided, the editor will preset the execution type and the user will not be able to change
   * it.
   */
  restrictedToExecutionType?: ProcessExecutionType;
  /**
   * The organization that the schema will be created for.
   * When this is provided, the editor will preset the organization and the user will not be able to change
   * it.
   */
  restrictedToOrganizationId?: string;
  /**
   * A callback to create the task schema with the specified parameters.
   * This is useful when you need to hit a different API endpoint than the standard schema creation endpoint or
   * or if you need to do additional validation or processing before creating the schema.
   * @param processTaskSchemaSpec the spec for the new process task schema
   */
  createProcessTaskSchema?: (
    processTaskSchemaSpec: ProcessSchemaDAO.CreateProcessTaskSchemaRequest
  ) => Promise<void>;
  /**
   * Whether the editor is being used as a standalone settings page.
   * Defaults to `true`
   * When true, this will include a title and navigation breadcrumbs. If this editor is embedded in another UI
   * (for example in a flowchart configuration) this should be false.
   */
  isSettingsPage?: boolean;
  /**
   * When true, the Create button sticks to the bottom of its scroll container.
   */
  stickyCreateButton?: boolean;
}

interface ProcessTaskSchemaEditorForEditProps {
  schemaId: string;
}

export type ProcessTaskSchemaEditorProps =
  | ProcessTaskSchemaEditorForCreateProps
  | ProcessTaskSchemaEditorForEditProps;

// exported for test only
const baseTestAttrs = {component: 'ProcessTaskSchemaEditorQuery'};
export const DATA_TEST_ATTRS = {
  action: {...baseTestAttrs, element: 'action'},
  schemaError: {...baseTestAttrs, element: 'schemaError'},
};

export function getMinimalEntitiesFromResponsibleParties(
  parties: Array<ProcessResponsibleParty>
): Array<MinimalEntity> {
  return parties.map((party) => ({
    id: party.id,
    handle: party.handle,
  }));
}

function isCreatingNewTaskSchema(
  props: ProcessTaskSchemaEditorProps
): props is ProcessTaskSchemaEditorForCreateProps {
  return props.schemaId == null;
}

function isEditingExistingTaskSchema(
  props: ProcessTaskSchemaEditorProps
): props is ProcessTaskSchemaEditorForEditProps {
  return props.schemaId != null;
}

function getResponsiblePartyLookupConfig(
  responsiblePartyLookupConfigs: Array<ProcessResponsiblePartyLookupConfig>
): LookupConfigSpec | null {
  assert(
    responsiblePartyLookupConfigs.length <= 1,
    `Expected at most one responsiblePartyLookupConfig ${responsiblePartyLookupConfigs}`
  );
  return responsiblePartyLookupConfigs[0] ?? null;
}

function handleNavigateToSchemaListing(): void {
  BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemasURL());
}

function makeProcessTaskSchemaSettingsPageContainerProps(
  props: BaseProcessTaskSchemaEditorContainerProps
): SettingsPageContainerProps {
  return {
    onClickTitle: handleNavigateToSchemaListing,
    subtitle: `${props.subtitle ?? 'Create Task Schema'}`,
    title: 'Task Schemas',
    toolbar: props.processTaskSchema && licenses.hasEnterprisePlan() && (
      <ProcessTaskSchemaHeaderActions processTaskSchema={props.processTaskSchema} />
    ),
  };
}

export function ProcessTaskSchemaHeaderActions(props: {
  processTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
}): JSX.Element {
  const actions = createProcessTaskSchemaHeaderActionsOptions(props.processTaskSchema);

  return (
    <OverflowMenu
      options={actions}
      enableTooltip={true}
      tooltipPlacement={FloatingPlacement.BOTTOM}
      menuProps={{dropLeft: true}}
      buttonProps={{
        size: ButtonSize.MEDIUM,
        dataTestAttrs: {
          component: 'ProcessTaskSchemaEditor',
          element: 'overflowMenu',
        },
      }}
    />
  );
}

// Adapter to bridge generic header actions props to ProcessTaskSchemaHeaderActions
export function DefaultProcessTaskSchemaHeaderActions(
  props: ProcessFlowchartConfigEditorHeaderActionsProps
): JSX.Element {
  return <ProcessTaskSchemaHeaderActions processTaskSchema={props.rootTaskSchema} />;
}

/** A page wrapper that includes the Task Schema's name and some navigation shortcuts */
function ProcessTaskSchemaEditorContainer(props: ProcessTaskSchemaEditorContainerProps): JSX.Element {
  return (
    <SettingsPageContainer {...makeProcessTaskSchemaSettingsPageContainerProps(props)}>
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section">{props.children}</div>
      </div>
    </SettingsPageContainer>
  );
}

/**
 * A page wrapper that includes the Task Schema's name and some navigation shortcuts, along with allowing multiple children for the various tabs
 */
export function ProcessTaskSchemaEditorTabbedContainer(
  props: ProcessTaskSchemaEditorTabbedContainerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<ProcessTaskSchemaTabs>(() => {
    const hashedTab = BenchlingUtil.getLocationHash();
    return enumIncludes(ProcessTaskSchemaTabs, hashedTab) ? hashedTab : ProcessTaskSchemaTabs.CONFIGURATION;
  });

  const onChangeTab = (tab: ProcessTaskSchemaTabs): void => {
    setActiveTab(tab);
    if (tab === ProcessTaskSchemaTabs.CONFIGURATION) {
      BenchlingUtil.clearLocationHash();
    } else {
      BenchlingUtil.setLocationHash(tab);
    }
  };

  const sectionClassName = classNames('settingsPageContainer-section', props.tabContentClassName);

  return (
    <TabContainer<ProcessTaskSchemaTabs>
      activeTab={activeTab}
      onRequestTabChange={onChangeTab}
      className="u-flex-grow u-min-width-zero"
      navClassName={props.tabNavClassName}
      paneClassName={props.tabPaneClassName}
    >
      <Tab tabKey={ProcessTaskSchemaTabs.CONFIGURATION} title="Configuration">
        <div className={sectionClassName}>{props.configurationTab}</div>
      </Tab>
      {isWorkflowCreateTaskFormsEnabled() && (
        <Tab tabKey={ProcessTaskSchemaTabs.FORMS} title="Forms">
          <div className={sectionClassName}>
            <ProcessTaskSchemaFormsList
              schemaId={props.schemaId}
              processTaskSchema={props.processTaskSchema!}
            />
          </div>
        </Tab>
      )}
      {BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS && (
        <Tab tabKey={ProcessTaskSchemaTabs.VALIDATION_RULES} title="Validation Rules">
          <div className={sectionClassName}>
            <QueryingValidationRulesTable
              hasValidationRulesItemId={props.schemaId}
              onSubmit={ProcessSchemaDAO.updateValidationRulesForProcessTaskSchema}
            />
          </div>
        </Tab>
      )}
      {BenchlingConfig.REQUESTS_V2_ENABLE &&
        isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}) && (
          <Tab tabKey={ProcessTaskSchemaTabs.REQUEST_V2_DEFINITIONS} title="Request Templates">
            <div className={sectionClassName}>
              <ProcessTaskSchemaRequestV2DefinitionsList taskSchemaId={props.schemaId} />
            </div>
          </Tab>
        )}
      {props.systemNamesTab != null && (
        <Tab tabKey={ProcessTaskSchemaTabs.SYSTEM_NAMES} title="System Names">
          <div className={sectionClassName}>{props.systemNamesTab}</div>
        </Tab>
      )}
    </TabContainer>
  );
}

export function FormChangeBanner(props: {numRelevantForms: number}): JSX.Element {
  return (
    <>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={`Updating this schema will have downstream effects on ${props.numRelevantForms} existing forms.`}
        supportingInfo="This could cause data integrity issues if the affected form changes are not reviewed, acknowledged, and/or updated."
      />
      <p className="u-small-spacing-top">
        Please contact the owner or responsible party for the corresponding forms that have been affected.
      </p>
    </>
  );
}

function UnpublishedExecutionTemplateBanner(props: {onGoToTemplate: () => void}): JSX.Element {
  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text="The execution template for this task schema is not published"
      supportingInfo="Execution templates must be published so users can execute tasks created with this schema"
      action={{
        actionType: InlineBannerActionType.CUSTOM,
        actionText: 'Go to template',
        actionOnClick: props.onGoToTemplate,
      }}
    />
  );
}

export function ArchivedTaskSchemasBanner(props: {archivedNames: Array<string>}): JSX.Element {
  const names = props.archivedNames.join(', ');
  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text="This flowchart uses one or more archived task schemas"
      supportingInfo={`Users can't create this flowchart until you replace or unarchive the following task schema(s): ${names}.`}
    />
  );
}

// Load plugins for the new flowchart config editor.
// NOTE: Registration is deferred to avoid a circular dependency:
// TaskSchemaNodeAdapters → DefaultTaskSchemaNodeDetailsSidepanelContent → ProcessTaskSchemaEditor
//   → DefaultProcessesPlugin → TaskSchemaNodeAdapters
let defaultPluginRegistered = false;
export function ensureDefaultProcessesPluginRegistered(): void {
  if (defaultPluginRegistered || !BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1) {
    return;
  }
  defaultPluginRegistered = true;
  const editorPlugin = new DefaultProcessesPlugin();
  editorPlugin.registerForEditor();
}

export function ProcessTaskSchemaEditorContent(props: ProcessTaskSchemaEditorContentProps): JSX.Element {
  const isBeingCreated = props.processTaskSchema == null;
  const isArchived = props.processTaskSchema?.archiveRecord != null;
  const isLocked = props.locked != null && props.locked;
  const [taskGroupPrefix, setTaskGroupPrefix] = useState(props.processTaskSchema?.taskGroupPrefix ?? '');
  const [processTaskSchemaName, setProcessTaskSchemaName] = useState(props.processTaskSchema?.name ?? '');
  const [processTaskSchemaSqlIdentifier, setProcessTaskSchemaSqlIdentifier] = useState(
    props.processTaskSchema?.sqlIdentifier
  );
  const [executionType, setExecutionType] = useState(
    props.processTaskSchema?.executionType ?? props.restrictedToExecutionType ?? ProcessExecutionType.ENTRY
  );
  const [taskSchemaPrefix, setTaskSchemaPrefix] = useState(
    props.processTaskSchema?.prefix ?? DEFAULT_TASK_SCHEMA_PREFIX
  );
  const [outputSchemaPrefix, setOutputSchemaPrefix] = useState(
    props.processTaskSchema?.outputSchema?.prefix ?? DEFAULT_OUTPUT_SCHEMA_PREFIX
  );
  const [hasOutputSchema, setHasOutputSchema] = useState(
    props.processTaskSchema == null || props.processTaskSchema.outputSchema != null
  );
  const schemaOwnerChoices = getSchemaOwnerChoices();
  const initialSchemaOwner =
    props.restrictedToOrganizationId != null
      ? schemaOwnerChoices.find(({id}) => id === props.restrictedToOrganizationId)
      : schemaOwnerChoices[0];

  const [schemaOwner, setSchemaOwner] = useState<EntitySummary | null>(
    props.processTaskSchema?.organization ?? initialSchemaOwner ?? null
  );
  const [taskFields, setTaskFields] = useState<Array<ClientSchemaField>>(
    props.processTaskSchema?.allFields ?? []
  );
  const [outputFields, setOutputFields] = useState<Array<ClientSchemaField>>(
    props.processTaskSchema?.outputSchema?.allFields ?? []
  );
  const [canSetAssigneeOnTaskCreation, setCanSetAssigneeOnTaskCreation] = useState(
    props.processTaskSchema?.canSetAssigneeOnTaskCreation ?? true
  );
  const [isPropagateWatchersEnabled, setIsPropagateWatchersEnabled] = useState(
    props.processTaskSchema?.isPropagateWatchersEnabled
  );
  const [autopopulateDirectOutputsFromTasks, setAutopopulateDirectOutputsFromTasks] = useState(
    Boolean(props.processTaskSchema?.outputSchema?.directExecutionModalAutopopulateTaskIds)
  );
  const [canOnlyBePartOfFlowchart, setCanOnlyBePartOfFlowchart] = useState(
    props.processTaskSchema?.canOnlyBePartOfFlowchart ?? false
  );
  const [canConfigureFlowchart, setCanConfigureFlowchart] = useState(
    props.processTaskSchema?.executionType === ProcessExecutionType.FLOWCHART
  );
  const [validRelevantForms, setValidRelevantForms] = useState(
    props.processTaskSchema?.relevantForms?.filter(
      // either we know the form is valid or the form is inaccesible, and we have to assume it may be valid
      (formOrInaccessible) =>
        isInaccessibleInfo(formOrInaccessible) || (formOrInaccessible as FormDefinitionNode).isValid
    ) || []
  );

  // TODO(BNCH-20942): remove these state variables
  // The field table maintains internal state which does not get rolled back on failed updates.
  // Here we set a key on the field tables which is reset when a field update fails.
  // This triggers a re-mount of the field table component which brings it back into sync with the actual
  // state of the schema.
  const [taskFieldTableKey, setTaskFieldTableKey] = useState(uuidv4());
  const [outputFieldTableKey, setOutputFieldTableKey] = useState(uuidv4());
  const [flowchartConfigTableKey, setFlowchartConfigTableKey] = useState(uuidv4());
  const [isEditingFlowchart, setIsEditingFlowchart] = useState(false);

  const [errors, setErrors] = useState<{[errorKey: string]: string}>({});
  const [defaultResponsibleParties, setDefaultResponsibleParties] = useState<Array<MinimalEntity>>(
    getMinimalEntitiesFromResponsibleParties(props.processTaskSchema?.responsibleParties ?? [])
  );
  const [areDefaultResponsiblePartiesValid, setAreDefaultResponsiblePartiesValid] = useState(true);
  const [showDefaultResponsiblePartiesError, setShowDefaultResponsiblePartiesError] = useState(false);
  const [responsiblePartyLookupConfig, setResponsiblePartyLookupConfig] = useState<LookupConfigSpec | null>(
    getResponsiblePartyLookupConfig(props.processTaskSchema?.responsiblePartyLookupConfigs ?? [])
  );

  const [taskFinderLookupConfig, setTaskFinderLookupConfig] = useState<LookupConfigSpec | null>(
    props.processTaskSchema?.outputSchema?.taskFinderLookupConfig ?? null
  );
  const [executionTemplate, setExecutionTemplate] = useState<EntryTemplateForConfigureButton>(
    props.processTaskSchema?.entryTemplate ?? null
  );

  const [defaultCreationFolderId, setDefaultCreationFolderId] = useState<string | null>(
    props.processTaskSchema?.defaultCreationFolderOrInaccessible != null &&
      !isInaccessible(props.processTaskSchema.defaultCreationFolderOrInaccessible)
      ? props.processTaskSchema.defaultCreationFolderOrInaccessible.id
      : null
  );
  const [isDefaultCreationFolderInaccessible, setIsDefaultCreationFolderInaccessible] = useState(
    props.processTaskSchema?.defaultCreationFolderOrInaccessible != null &&
      isInaccessible(props.processTaskSchema.defaultCreationFolderOrInaccessible)
  );
  const [defaultEntryExecutionFolderId, setDefaultEntryExecutionFolderId] = useState<string | null>(
    props.processTaskSchema?.defaultEntryExecutionFolderOrInaccessible != null &&
      !isInaccessible(props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible)
      ? props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible.id
      : null
  );
  const [isDefaultEntryExecutionFolderInaccessible, setIsDefaultEntryExecutionFolderInaccessible] = useState(
    props.processTaskSchema?.defaultEntryExecutionFolderOrInaccessible != null &&
      isInaccessible(props.processTaskSchema.defaultEntryExecutionFolderOrInaccessible)
  );

  const [taskGroupInstructions, setTaskGroupInstructions] = useState(
    props.processTaskSchema?.taskGroupInstructions
  );

  const {statusLifecycles} = props;
  const statusLifecyclesForExecutionType = useMemo(() => {
    const lifecycles = (statusLifecycles ?? [])
      .filter(({schemaExecutionType}) => schemaExecutionType === executionType)
      .map((statusLifecycle) => ({...statusLifecycle, name: strUtil.toSentenceCase(statusLifecycle.name)}));
    assert(
      statusLifecycles == null || lifecycles.length > 0,
      `Expected at least one status lifecycle for executionType ${executionType}`
    );
    return lifecycles;
  }, [statusLifecycles, executionType]);
  const [statusLifecycle, setStatusLifecycle] = useState<{id: string; name: string}>(
    props.processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]
  );
  useEffect(() => {
    setStatusLifecycle(props.processTaskSchema?.statusLifecycle ?? statusLifecyclesForExecutionType[0]);
  }, [statusLifecyclesForExecutionType, props.processTaskSchema]);
  useEffect(() => {
    if (props.processTaskSchema?.statusLifecycle == null) {
      setStatusLifecycle(statusLifecyclesForExecutionType[0]);
    }
    if (executionType !== ProcessExecutionType.ENTRY) {
      setExecutionTemplate(null);
      setDefaultEntryExecutionFolderId(null);
    }
    // statusLifecyclesForExecutionType is updated when executionType updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionType]);

  const {tagSchemas, taskSchemas, storableSchemas, resultSchemas} = props;
  const haveSchemasLoaded = [tagSchemas, taskSchemas, storableSchemas, resultSchemas].every(
    (arr) => arr != null
  );
  const allSchemasById = useMemo(() => {
    const schemasById = [...(tagSchemas ?? []), ...(storableSchemas ?? []), ...(resultSchemas ?? [])].reduce(
      (acc, schema) => {
        acc[schema.id] = {...schema, fields: schema.fieldsJson, allFields: schema.allFieldsJson};
        return acc;
      },
      {} as SchemaById
    );

    for (const taskSchema of taskSchemas ?? []) {
      schemasById[taskSchema.id] = taskSchema;
      if (taskSchema.outputSchema != null) {
        schemasById[taskSchema.outputSchema.id] = taskSchema.outputSchema;
      }
    }
    if (props.processTaskSchema != null) {
      schemasById[props.processTaskSchema.id] = props.processTaskSchema;
      if (props.processTaskSchema.outputSchema != null) {
        schemasById[props.processTaskSchema.outputSchema.id] = props.processTaskSchema.outputSchema;
      }
    }
    return schemasById;
  }, [tagSchemas, storableSchemas, resultSchemas, taskSchemas, props.processTaskSchema]);

  // We should hide section numbers / names if there is only one section left to show
  const shouldHideSectionHeaders = useMemo(
    () =>
      props.sectionsToHide != null &&
      Object.values(ProcessTaskSchemaEditorContentSections).length - (props.sectionsToHide.length ?? 0) <= 1,
    [props.sectionsToHide]
  );

  const shouldHideSection = useCallback(
    (section: ProcessTaskSchemaEditorContentSections): boolean =>
      props.sectionsToHide != null && props.sectionsToHide.includes(section),
    [props.sectionsToHide]
  );

  const toggleHasOutputSchemaButton = (): void => {
    if (hasOutputSchema) {
      runModal(RemoveOutputSchemaModal, {
        onRequestSubmit: () => {
          setHasOutputSchema(false);
          setOutputSchemaPrefix(DEFAULT_OUTPUT_SCHEMA_PREFIX);
          setOutputFields([]);
        },
      });
    } else {
      setHasOutputSchema(true);
    }
  };

  const updateProcessTaskSchemaWithFields = useCallback(
    (params: UpdateProcessSchema & {updateTables?: boolean}) => {
      if (props.taskDefinitionId != null) {
        return updateProcessTaskSchemaForTaskDefinitionVersion(
          params,
          props.taskDefinitionId,
          setStateOnUpdate,
          setErrors,
          props.processTaskSchema,
          hasOutputSchema,
          props.afterTaskSchemaUpdate
        );
      } else {
        return updateProcessTaskSchema(
          params,
          setStateOnUpdate,
          setErrors,
          props.processTaskSchema,
          hasOutputSchema,
          props.afterTaskSchemaUpdate
            ? (taskSchema) => props.afterTaskSchemaUpdate?.(taskSchema, null)
            : undefined
        );
      }
    },
    [props, hasOutputSchema]
  );

  const onFieldsChanged = async (
    oldFields: Array<ClientSchemaField>,
    newFields: Array<ClientSchemaField>,
    fieldsKey: 'fields' | 'taskFields' | 'outputFields',
    setLocalFields: (fields: Array<ClientSchemaField>) => void
  ): Promise<void> => {
    let fieldsChanged;
    try {
      fieldsChanged = haveFieldsChanged(oldFields, newFields);
    } catch (e) {
      if (e instanceof FieldNamingException) {
        notifications.error(e.message);
        return;
      } else {
        throw e;
      }
    }

    if (!fieldsChanged) {
      return;
    }

    if (!isBeingCreated) {
      const newOrUpdatedSnapshotFields = getNewOrUpdatedSnapshotFields(oldFields, newFields);
      if (newOrUpdatedSnapshotFields.length > 0) {
        await createOrUpdateSnapshotField(
          newOrUpdatedSnapshotFields,
          newFields,
          fieldsKey,
          setErrors,
          setStateOnUpdate,
          props.refetchProcessSchema,
          props.processTaskSchema
        );
      } else if (props.taskDefinitionId != null) {
        await updateProcessTaskSchemaForTaskDefinitionVersion(
          {[fieldsKey]: newFields},
          props.taskDefinitionId,
          setStateOnUpdate,
          setErrors,
          props.processTaskSchema,
          hasOutputSchema,
          props.afterTaskSchemaUpdate
        );
      } else {
        await updateProcessTaskSchema(
          {[fieldsKey]: newFields},
          setStateOnUpdate,
          setErrors,
          props.processTaskSchema,
          hasOutputSchema,
          (taskSchema) => props.afterTaskSchemaUpdate?.(taskSchema, null)
        );
      }
    } else {
      setLocalFields(newFields);
    }
  };

  const validationErrorMessage = ((): string | null => {
    if (schemaOwner == null) {
      return 'Enter an owner before creating this schema.';
    }
    if (
      isEmptyOrWhitespace(taskGroupPrefix) ||
      isEmptyOrWhitespace(processTaskSchemaName) ||
      isEmptyOrWhitespace(taskSchemaPrefix)
    ) {
      return 'Enter prefixes and a schema name before creating this schema.';
    }
    if (taskFields.length === 0) {
      return 'Enter Task fields before creating this schema.';
    }
    if (hasOutputSchema) {
      if (isEmptyOrWhitespace(outputSchemaPrefix)) {
        return 'Enter an Output prefix before creating this schema.';
      }
      if (outputFields.length === 0) {
        return 'This schema has an Output schema. Enter Output fields before creating';
      }
    }
    if (executionType === ProcessExecutionType.ENTRY && executionTemplate == null) {
      return 'Enter an Execution template before creating this schema.';
    }
    return null;
  })();

  const createProcessTaskSchema = async (): Promise<void> => {
    setErrors({});
    if (!areDefaultResponsiblePartiesValid) {
      setShowDefaultResponsiblePartiesError(!areDefaultResponsiblePartiesValid);
      return;
    }
    // TODO: add default responsible parties to create payload
    const baseRequestData = {
      name: processTaskSchemaName,
      sqlIdentifier: processTaskSchemaSqlIdentifier,
      taskGroupPrefix,
      prefix: taskSchemaPrefix,
      executionType,
      clientOrigin: ProcessClientOrigin.PROCESSES,
      canSetAssigneeOnTaskCreation,
      schemaFields: massageFieldsForSubmit(taskFields),
      organizationId: schemaOwner!.id,
      defaultResponsiblePartiesIds: defaultResponsibleParties.map(({id}) => id),
      statusLifecycleId: statusLifecycle.id,
      defaultCreationFolderId,
      taskGroupInstructions,
      isPropagateWatchersEnabled,
      processOutputSchema: hasOutputSchema
        ? {
            prefix: outputSchemaPrefix,
            schemaFields: massageFieldsForSubmit(outputFields),
          }
        : null,
    };
    let requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest;
    if (executionType === ProcessExecutionType.ENTRY) {
      assert(
        executionTemplate != null && isEntryTemplatePreCreate(executionTemplate),
        'Expected execution template to be configured for entry type schema'
      );
      requestData = {
        ...baseRequestData,
        executionType,
        executionTemplate: {
          templateCollectionId: executionTemplate?.templateCollectionId,
          templateName: executionTemplate?.name,
          sourceTemplateId: executionTemplate?.sourceTemplateId,
          publishOnSchemaCreation: executionTemplate?.publishOnSchemaCreation,
        },
        defaultEntryExecutionFolderId,
        canOnlyBePartOfFlowchart,
      };
    } else if (executionType === ProcessExecutionType.DIRECT) {
      requestData = {...baseRequestData, executionType, canOnlyBePartOfFlowchart};
      if (hasOutputSchema) {
        requestData.processOutputSchema!.autopopulateFromTasks = autopopulateDirectOutputsFromTasks;
      }
    } else if (executionType === ProcessExecutionType.FLOWCHART) {
      requestData = {...baseRequestData, executionType};
    } else {
      assert(false, `Unsupported execution type - ${executionType}`);
    }
    try {
      if (props.createProcessTaskSchema != null) {
        await props.createProcessTaskSchema(requestData);
      } else {
        const processTaskSchema = await withLoadingScreen(
          ProcessSchemaDAO.createProcessTaskSchema(requestData),
          SAVING_TASK_SCHEMA_MESSAGE
        );
        cacheUpdatedTaskSchema(processTaskSchema);
        BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemaURL(processTaskSchema.id));
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON) {
        setErrors(e.responseJSON.errors ?? {});
      }
      notifications.notifyError(e);
    }
  };

  const setStateOnUpdate = (params: UpdateProcessSchema & {updateTables?: boolean}): void => {
    if (params.taskGroupPrefix != null) {
      setTaskGroupPrefix(params.taskGroupPrefix);
    }
    if (params.name != null) {
      setProcessTaskSchemaName(params.name);
    }
    if (params.sqlIdentifier != null) {
      setProcessTaskSchemaSqlIdentifier(params.sqlIdentifier);
    }
    if (params.taskSchemaPrefix != null) {
      setTaskSchemaPrefix(params.taskSchemaPrefix);
    }
    if (params.taskFields != null) {
      setTaskFields(params.taskFields);
      // locally mark all forms as "invalidated" by clearing out the cached value from state
      setValidRelevantForms([]);
      if (params.updateTables) {
        setTaskFieldTableKey(uuidv4());
      }
    }
    if (params.outputSchemaPrefix != null) {
      setOutputSchemaPrefix(params.outputSchemaPrefix);
    }
    if (params.outputFields != null) {
      setOutputFields(params.outputFields);
      if (params.updateTables) {
        setOutputFieldTableKey(uuidv4());
      }
    }
    if (params.autopopulateDirectOutputsFromTasks != null) {
      setAutopopulateDirectOutputsFromTasks(params.autopopulateDirectOutputsFromTasks);
    }
    if (params.defaultResponsibleParties !== undefined) {
      setDefaultResponsibleParties(params.defaultResponsibleParties);
    }
    if (params.responsiblePartyLookupConfig !== undefined) {
      setResponsiblePartyLookupConfig(params.responsiblePartyLookupConfig);
    }
    if (params.updateTaskFinder) {
      setTaskFinderLookupConfig(params.taskFinderLookupConfig ?? null);
    }
    if (params.canSetAssigneeOnTaskCreation != null) {
      setCanSetAssigneeOnTaskCreation(params.canSetAssigneeOnTaskCreation);
    }
    if (params.canOnlyBePartOfFlowchart != null) {
      setCanOnlyBePartOfFlowchart(params.canOnlyBePartOfFlowchart);
    }
    if (params.defaultCreationFolderId !== undefined) {
      setDefaultCreationFolderId(params.defaultCreationFolderId);
    }
    if (params.defaultEntryExecutionFolderId !== undefined) {
      setDefaultEntryExecutionFolderId(params.defaultEntryExecutionFolderId);
    }
    if (params.taskGroupInstructions !== undefined) {
      setTaskGroupInstructions(params.taskGroupInstructions);
    }
    if (params.isPropagateWatchersEnabled != null) {
      setIsPropagateWatchersEnabled(params.isPropagateWatchersEnabled);
    }
    if (params.flowchartConfig !== undefined && params.updateTables) {
      setFlowchartConfigTableKey(uuidv4());
    }
  };

  // Only generate a prefix preview when the task group prefix is not empty
  const generatePrefixPreview = (label: string, prefix: string): string =>
    isEmptyOrWhitespace(taskGroupPrefix) ? '' : `${label}: ${taskGroupPrefix}1-${prefix}1`;

  const handleDefaultResponsiblePartiesUpdate = async (
    newDefaultResponsibleParties: Array<MinimalEntity>
  ): Promise<void> => {
    if (isEqual(newDefaultResponsibleParties, defaultResponsibleParties)) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchemaWithFields({
        defaultResponsibleParties: newDefaultResponsibleParties,
      });
    } else {
      setDefaultResponsibleParties(newDefaultResponsibleParties);
    }
  };

  const handleResponsiblePartyLookupConfigUpdate = async (
    newResponsiblePartyLookupConfig: LookupConfigSpec | null
  ): Promise<void> => {
    if (isEqual(newResponsiblePartyLookupConfig, responsiblePartyLookupConfig)) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchemaWithFields({
        responsiblePartyLookupConfig: newResponsiblePartyLookupConfig,
      });
    } else {
      setResponsiblePartyLookupConfig(newResponsiblePartyLookupConfig);
    }
  };

  const handleDefaultCreationFolderIdUpdate = async (
    newDefaultCreationFolderId: string | null
  ): Promise<void> => {
    if (newDefaultCreationFolderId === defaultCreationFolderId) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchemaWithFields({
        defaultCreationFolderId: newDefaultCreationFolderId,
      });
      setIsDefaultCreationFolderInaccessible(false);
    } else {
      setDefaultCreationFolderId(newDefaultCreationFolderId);
    }
  };

  const handleDefaultEntryExecutionFolderIdUpdate = async (
    newDefaultEntryExecutionFolderId: string | null
  ): Promise<void> => {
    if (newDefaultEntryExecutionFolderId === defaultEntryExecutionFolderId) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchemaWithFields({
        defaultEntryExecutionFolderId: newDefaultEntryExecutionFolderId,
      });
      setIsDefaultEntryExecutionFolderInaccessible(false);
    } else {
      setDefaultEntryExecutionFolderId(newDefaultEntryExecutionFolderId);
    }
  };

  const handleTaskGroupInstructionsUpdate = async (newTaskGroupInstructions: string): Promise<void> => {
    if (newTaskGroupInstructions === taskGroupInstructions) {
      return;
    }
    if (!isBeingCreated) {
      await updateProcessTaskSchemaWithFields({
        taskGroupInstructions: newTaskGroupInstructions,
      });
    } else {
      setTaskGroupInstructions(newTaskGroupInstructions);
    }
  };

  if (schemaOwner == null) {
    return <NoOwnersSchemaMessage pluralName="workflow task schemas" />;
  }

  const shouldShowTemplateUnpublishedBanner = (): boolean => {
    if (isBeingCreated) {
      return false;
    } else {
      return isExecutionBlockedByTemplatePublishing(props.processTaskSchema!);
    }
  };

  const flowchartArchivedTaskSchemas = (() => {
    const fcv = props.processTaskSchema?.flowchartConfig?.currentFlowchartConfigVersion;
    if (fcv == null || isInaccessible(fcv)) {
      return [];
    }
    return fcv.availableSchemas.filter(
      (schema) => !isInaccessibleInfo(schema) && schema.archiveRecord != null
    );
  })();

  const shouldShowTemplateArchivedBanner = (): boolean => {
    if (isBeingCreated) {
      return false;
    } else {
      return flowchartArchivedTaskSchemas.length > 0;
    }
  };

  return (
    <>
      {shouldShowTemplateUnpublishedBanner() && (
        <div className={css(styles.configurationWarningBanner)}>
          <UnpublishedExecutionTemplateBanner
            onGoToTemplate={() => {
              BenchlingUtil.navigate(null, props.processTaskSchema!.entryTemplate!.editUrl);
            }}
          />
        </div>
      )}
      {shouldShowTemplateArchivedBanner() && (
        <div className={css(styles.configurationWarningBanner)}>
          <ArchivedTaskSchemasBanner
            archivedNames={flowchartArchivedTaskSchemas
              .map((taskSchema) => (isInaccessibleInfo(taskSchema) ? '(Inaccessible)' : taskSchema.name))
              .filter((n) => n.length > 0)}
          />
        </div>
      )}
      {!shouldHideSection(ProcessTaskSchemaEditorContentSections.TASK) && (
        <ProcessTaskSchemaEditorSection
          sectionTitle="Task"
          sectionNumber={1}
          shouldHideSectionHeader={shouldHideSectionHeaders}
          noSectionSpacing={props.noSectionSpacing}
        >
          <ProcessSchemaMetadataSectionRow>
            <PrefixField
              label="Task group prefix"
              value={taskGroupPrefix}
              error={errors.taskGroupPrefix ?? ''}
              isBeingCreated={isBeingCreated}
              isDisabled={
                isLocked ||
                isArchived ||
                isEditingFlowchart ||
                (!isBeingCreated && BenchlingConfig.WORKFLOWS_DISABLE_CHANGING_TASK_GROUP_PREFIX)
              }
              onChange={async (updatedTaskGroupPrefix: string) => {
                if (updatedTaskGroupPrefix === taskGroupPrefix) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    taskGroupPrefix: updatedTaskGroupPrefix,
                  });
                } else {
                  setTaskGroupPrefix(updatedTaskGroupPrefix);
                }
              }}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'prefixField'}}
            />
            <NameField
              label="Name"
              value={processTaskSchemaName}
              error={errors.processTaskSchemaName ?? ''}
              isBeingCreated={isBeingCreated}
              isDisabled={isArchived || isEditingFlowchart || isLocked}
              onChange={async (updatedName: string) => {
                if (updatedName === processTaskSchemaName) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    name: updatedName,
                  });
                } else {
                  setProcessTaskSchemaName(updatedName);
                }
              }}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'nameField'}}
            />
            <OwnerField
              className={css(styles.ownerField)}
              isEditable={isBeingCreated && props.restrictedToOrganizationId == null}
              ownerChoices={schemaOwnerChoices}
              owner={schemaOwner}
              onOwnerChanged={setSchemaOwner}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'ownerField'}}
            />
          </ProcessSchemaMetadataSectionRow>
          <ProcessSchemaMetadataSectionRow>
            <PrefixField
              label="ID prefix"
              value={taskSchemaPrefix}
              error={errors.taskSchemaPrefix ?? ''}
              isBeingCreated={isBeingCreated}
              isDisabled={isArchived || isEditingFlowchart || isLocked}
              onChange={async (updatedTaskSchemaPrefix: string) => {
                if (updatedTaskSchemaPrefix === taskSchemaPrefix) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    taskSchemaPrefix: updatedTaskSchemaPrefix,
                  });
                } else {
                  setTaskSchemaPrefix(updatedTaskSchemaPrefix);
                }
              }}
              prefixPreview={generatePrefixPreview('Task ID preview', taskSchemaPrefix)}
              dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskPrefixField'}}
            />
            <ExecutionTypeField
              value={executionType}
              onChange={(newExecutionType: ProcessExecutionType) => {
                setExecutionType(newExecutionType);
                setCanConfigureFlowchart(newExecutionType === ProcessExecutionType.FLOWCHART);
              }}
              isEditable={isBeingCreated && props.restrictedToExecutionType == null}
            />
            {statusLifecycle ? (
              <StatusLifecycleField
                value={statusLifecycle}
                onChange={setStatusLifecycle}
                options={statusLifecyclesForExecutionType}
                isBeingCreated={isBeingCreated}
                error={errors.statusLifecycle}
              />
            ) : (
              <div className="fieldInput-label-container">
                <FieldLabel htmlFor="" label="Status lifecycle" />
                <div className="u-small-spacing-top">
                  <HorizontalThrobber />
                </div>
              </div>
            )}
          </ProcessSchemaMetadataSectionRow>
          <ProcessSchemaMetadataSectionRow>
            <ConfigureExecutionTemplateSection
              executionType={executionType}
              executionTemplate={executionTemplate}
              onChange={(onTemplateChangeArgs) => {
                setExecutionTemplate({
                  templateCollectionId: onTemplateChangeArgs.templateCollectionId,
                  name: onTemplateChangeArgs.newTemplateName,
                  sourceTemplateId: onTemplateChangeArgs.sourceTemplateId,
                  publishOnSchemaCreation: onTemplateChangeArgs.publishOnSchemaCreation,
                });
              }}
              isDisabled={!isBeingCreated || isArchived || isLocked}
              error={errors.sourceTemplate}
            />
            <DefaultCreationFolderSelectorContainer
              executionType={executionType}
              defaultCreationFolderId={defaultCreationFolderId}
              isInaccessible={isDefaultCreationFolderInaccessible}
              onChange={handleDefaultCreationFolderIdUpdate}
              isDisabled={isArchived || isLocked}
            />
            <DefaultEntryExecutionFolderSelectorContainer
              executionType={executionType}
              defaultEntryExecutionFolderId={defaultEntryExecutionFolderId}
              isInaccessible={isDefaultEntryExecutionFolderInaccessible}
              onChange={handleDefaultEntryExecutionFolderIdUpdate}
              isDisabled={isArchived || isLocked}
            />
          </ProcessSchemaMetadataSectionRow>
          <ProcessSchemaMetadataSectionRow>
            <PartiesField
              label="Default responsible parties"
              value={defaultResponsibleParties}
              onChange={handleDefaultResponsiblePartiesUpdate}
              isBeingCreated={isBeingCreated}
              isDisabled={isArchived || isEditingFlowchart || isLocked}
              setDoesNotHaveUnresolvedInput={setAreDefaultResponsiblePartiesValid}
              showError={showDefaultResponsiblePartiesError}
              dataTestAttrs={{
                component: 'ProcessTaskSchemaEditor',
                element: 'defaultResponsiblePartiesField',
              }}
              errorText='Invalid "Default responsible parties" value'
            />
            <CanSetAssigneeOnTaskCreationField
              value={canSetAssigneeOnTaskCreation}
              disabled={isEditingFlowchart || isLocked}
              hideWithMessage={
                executionType === ProcessExecutionType.FLOWCHART
                  ? 'Flowchart tasks do not have assignees.'
                  : null
              }
              onChange={async (value) => {
                if (value === canSetAssigneeOnTaskCreation) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    canSetAssigneeOnTaskCreation: value,
                  });
                } else {
                  setCanSetAssigneeOnTaskCreation(value);
                }
              }}
            />
            <CanOnlyBePartOfFlowchartField
              executionType={executionType}
              value={canOnlyBePartOfFlowchart}
              error={errors.canOnlyBePartOfFlowchart}
              disabled={isEditingFlowchart || isLocked}
              onChange={async (value) => {
                if (value === canOnlyBePartOfFlowchart) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    canOnlyBePartOfFlowchart: value,
                  });
                } else {
                  setCanOnlyBePartOfFlowchart(value);
                }
              }}
            />
          </ProcessSchemaMetadataSectionRow>
          {hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) && (
            <ProcessSchemaMetadataSectionRow>
              <ResponsiblePartyLookupConfigField
                value={responsiblePartyLookupConfig}
                isBeingCreated={isBeingCreated}
                isDisabled={isArchived || isEditingFlowchart || isLocked}
                isLoading={!haveSchemasLoaded}
                label="Task to responsible party lookup"
                onChange={handleResponsiblePartyLookupConfigUpdate}
                taskSchema={props.processTaskSchema}
                error={errors.responsiblePartyLookupConfigs}
              />
            </ProcessSchemaMetadataSectionRow>
          )}
          {BenchlingConfig.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME && (
            <ProcessSchemaMetadataSectionRow>
              <SchemaWarehouseNameInput
                isWritable={
                  BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME &&
                  !isArchived &&
                  !isEditingFlowchart &&
                  !isLocked
                }
                schema={props.processTaskSchema}
                sqlIdentifier={processTaskSchemaSqlIdentifier ?? ''}
                onSqlIdentifierChange={async (updatedSqlIdentifier: string) => {
                  if (updatedSqlIdentifier === processTaskSchemaSqlIdentifier) {
                    return;
                  }
                  if (!isBeingCreated) {
                    await updateProcessTaskSchemaWithFields({
                      sqlIdentifier: updatedSqlIdentifier,
                    });
                  } else {
                    setProcessTaskSchemaSqlIdentifier(updatedSqlIdentifier);
                  }
                }}
                error={errors.sqlIdentifier}
                dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'schemaWarehouseName'}}
              />
            </ProcessSchemaMetadataSectionRow>
          )}
          <ProcessSchemaMetadataSectionRow>
            <IsPropagateWatchersEnabledField
              value={isPropagateWatchersEnabled}
              onChange={async (value) => {
                if (value === isPropagateWatchersEnabled) {
                  return;
                }
                if (!isBeingCreated) {
                  await updateProcessTaskSchemaWithFields({
                    isPropagateWatchersEnabled: value,
                  });
                } else {
                  setIsPropagateWatchersEnabled(value);
                }
              }}
              disabled={isArchived || isEditingFlowchart || isLocked}
              executionType={executionType}
            />
          </ProcessSchemaMetadataSectionRow>
          <ProcessTaskGroupInstructionsField
            label="Instructions"
            value={taskGroupInstructions ?? ''}
            isBeingCreated={isBeingCreated}
            onChange={handleTaskGroupInstructionsUpdate}
            error={errors.taskGroupInstructions}
            isDisabled={isArchived || isEditingFlowchart || isLocked}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskGroupInstructionsField'}}
          />
          {!shouldHideSection(ProcessTaskSchemaEditorContentSections.TASK_FIELDS) && (
            <>
              <ProcessSchemaErrorList error={errors.taskFields ?? ''} />
              <ProcessSchemaFieldTable
                isDisabled={isArchived || isEditingFlowchart || isLocked}
                key={taskFieldTableKey}
                hasBottomLink={!hasOutputSchema}
                schema={props.processTaskSchema ?? null}
                ownerUrlId={schemaOwner.url_identifier}
                ownerId={schemaOwner.id}
                registryId={props.registryId}
                onFieldsChange={(updatedTaskFields) => {
                  onFieldsChanged(taskFields, updatedTaskFields, 'taskFields', setTaskFields);
                }}
                schemaType="task"
                storableSchemas={props.storableSchemas}
                fields={taskFields}
                resultSchemas={props.resultSchemas}
                dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskTable'}}
                // if we edit the task fields, and there are any valid forms, warn the user that they are going to
                // invalidate forms with their actions
                changeWarnings={
                  isWorkflowCreateTaskFormsEnabled() && !isBeingCreated && validRelevantForms.length
                    ? [<FormChangeBanner numRelevantForms={validRelevantForms.length} />]
                    : undefined
                }
                compactMode={props.isSettingsPage === false}
              />
            </>
          )}
        </ProcessTaskSchemaEditorSection>
      )}
      {!shouldHideSection(ProcessTaskSchemaEditorContentSections.OUTPUT) && (
        <ProcessTaskSchemaEditorSection
          isLastSection={!canConfigureFlowchart}
          sectionTitle="Output"
          sectionNumber={2}
          actionButton={
            !isBeingCreated && props.processTaskSchema?.outputSchema?.id
              ? {
                  onClick: () => {
                    copyToClipboard(props.processTaskSchema!.outputSchema!.id, {
                      showToastOnSuccess: true,
                      toastText: 'Copied output schema API ID to clipboard',
                    });
                  },
                  tooltipTitle: 'Copy API ID',
                  icon: 'note-code',
                }
              : undefined
          }
          shouldHideSectionHeader={shouldHideSectionHeaders}
        >
          {hasOutputSchema && (
            <>
              <ProcessSchemaMetadataSectionRow>
                <PrefixField
                  label="ID prefix"
                  value={outputSchemaPrefix}
                  error={errors.outputSchemaPrefix ?? ''}
                  isBeingCreated={isBeingCreated}
                  isDisabled={isArchived || isEditingFlowchart || isLocked}
                  onChange={async (updatedOutputSchemaPrefix: string) => {
                    if (updatedOutputSchemaPrefix === outputSchemaPrefix) {
                      return;
                    }
                    if (!isBeingCreated) {
                      await updateProcessTaskSchemaWithFields({
                        outputSchemaPrefix: updatedOutputSchemaPrefix,
                      });
                    } else {
                      setOutputSchemaPrefix(updatedOutputSchemaPrefix);
                    }
                  }}
                  prefixPreview={generatePrefixPreview('Output ID preview', outputSchemaPrefix)}
                  dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputPrefixField'}}
                />
              </ProcessSchemaMetadataSectionRow>
              {executionType === ProcessExecutionType.DIRECT && (
                <ProcessSchemaMetadataSectionRow>
                  <AutopopulateFromTasksField
                    value={autopopulateDirectOutputsFromTasks}
                    disabled={isLocked}
                    onChange={async (value) => {
                      if (value === autopopulateDirectOutputsFromTasks) {
                        return;
                      }
                      if (!isBeingCreated) {
                        await updateProcessTaskSchemaWithFields({
                          autopopulateDirectOutputsFromTasks: value,
                        });
                      } else {
                        setAutopopulateDirectOutputsFromTasks(value);
                      }
                    }}
                  />
                </ProcessSchemaMetadataSectionRow>
              )}
              <ProcessSchemaErrorList error={errors.outputFields ?? ''} />
              <ProcessSchemaFieldTable
                isDisabled={isArchived || isEditingFlowchart || isLocked}
                key={outputFieldTableKey}
                hasBottomLink={hasOutputSchema}
                schema={props.processTaskSchema?.outputSchema ?? null}
                ownerId={schemaOwner.id}
                ownerUrlId={schemaOwner.url_identifier}
                registryId={props.registryId}
                onFieldsChange={(updatedOutputFields) => {
                  onFieldsChanged(outputFields, updatedOutputFields, 'outputFields', setOutputFields);
                }}
                schemaType="output"
                storableSchemas={props.storableSchemas}
                fields={outputFields}
                resultSchemas={props.resultSchemas}
                dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputTable'}}
                compactMode={props.isSettingsPage === false}
              />
              {EXECUTION_TYPES_WITH_TASK_FINDER.has(executionType) && (
                <ConfigureTaskFinderSection
                  isBeingCreated={isBeingCreated}
                  isLoading={!haveSchemasLoaded}
                  taskFinderLookupConfig={taskFinderLookupConfig}
                  outputSchema={props.processTaskSchema?.outputSchema ?? null}
                  taskSchema={props.processTaskSchema ?? null}
                  isArchived={isArchived}
                  error={errors.taskFinderLookupConfig}
                  onChange={(updatedLookupConfig) => {
                    updateProcessTaskSchemaWithFields({
                      taskFinderLookupConfig: updatedLookupConfig,
                      updateTaskFinder: true,
                    });
                  }}
                  locked={isLocked}
                />
              )}
            </>
          )}
          {isBeingCreated && (
            <ToggleOutputSchemaButton
              hasOutputSchema={hasOutputSchema}
              onClick={toggleHasOutputSchemaButton}
            />
          )}
          {!hasOutputSchema && !isBeingCreated && (
            <div className={css(styles.descriptionText)}>
              Output schema cannot be added after the task schema is created.
            </div>
          )}
        </ProcessTaskSchemaEditorSection>
      )}
      {canConfigureFlowchart &&
        // We should never show the flowchart config editor when flowchart first config is enabled since
        // the flowchart config editor should be the main entrypoint for editing flowcharts.
        // We expect this component to be embedded in the flowchart config editor to edit the fields on the
        // root and output nodes.
        !BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1 &&
        !shouldHideSection(ProcessTaskSchemaEditorContentSections.FLOWCHART) && (
          <ProcessTaskSchemaEditorSection
            isLastSection={true}
            sectionTitle="Flowchart"
            sectionNumber={3}
            shouldHideSectionHeader={shouldHideSectionHeaders}
          >
            {isBeingCreated ? (
              <span className={css(styles.descriptionText, styles.flowchartText)}>
                Configure the flowchart after the task schema is created.
              </span>
            ) : props.isAnyPartOfFlowchartConfigInaccessible ? (
              // TODO(BNCH-49564): Attempt to show this user a static view of the FC, even  if they can't edit it
              <ProcessSchemaErrorList error="You do not have access to all of the flowchart data.  Please contact your administrator for assistance." />
            ) : (
              <>
                <ProcessSchemaErrorList error={errors.flowchartConfig ?? ''} />
                <GraphBuilderFlowchartConfigEditor
                  key={flowchartConfigTableKey}
                  rootTaskSchema={props.processTaskSchema!}
                  flowchartConfig={
                    props.processTaskSchema!.flowchartConfig?.currentFlowchartConfigVersion ?? null
                  }
                  allSchemasById={allSchemasById}
                  allTaskSchemas={props.taskSchemas}
                  isEditing={isEditingFlowchart}
                  onSetIsEditing={setIsEditingFlowchart}
                  onSubmit={(
                    flowchartConfig,
                    configEditorEventGroupId,
                    didInitialFlowchartHaveChainedRouters
                  ) => {
                    updateProcessTaskSchemaWithFields({
                      flowchartConfig,
                      configEditorEventGroupId,
                      didInitialFlowchartHaveChainedRouters,
                    });
                  }}
                />
              </>
            )}
          </ProcessTaskSchemaEditorSection>
        )}
      {props.processTaskSchema == null &&
        (() => {
          const createButton = (
            <Button
              className={!props.stickyCreateButton ? css(styles.createButton) : undefined}
              buttonStyle={ButtonStyle.SECONDARY}
              disabled={validationErrorMessage != null}
              onClick={createProcessTaskSchema}
              size={ButtonSize.MEDIUM}
              text="Create"
              dataTestAttrs={DATA_TEST_ATTRS.action}
            />
          );
          return props.stickyCreateButton ? (
            <div className={css(styles.stickyCreateButtonFooter)}>
              {validationErrorMessage ? (
                <div
                  className={css(styles.stickyCreateButtonFooterMessage)}
                  {...getTestProps(DATA_TEST_ATTRS.schemaError)}
                >
                  <InlineBanner bannerType={InlineBannerType.WARNING} text={validationErrorMessage} />
                </div>
              ) : (
                <span className={css(styles.stickyCreateButtonFooterMessage)}>
                  Create this task schema to save your edits.
                </span>
              )}
              {createButton}
            </div>
          ) : (
            <>
              {createButton}
              {validationErrorMessage && (
                <div
                  className={css(styles.schemaErrorMessage)}
                  {...getTestProps(DATA_TEST_ATTRS.schemaError)}
                >
                  <InlineBanner bannerType={InlineBannerType.WARNING} text={validationErrorMessage} />
                </div>
              )}
            </>
          );
        })()}
    </>
  );
}

function ToggleOutputSchemaButton(props: ToggleOutputSchemaButtonProps): JSX.Element {
  return (
    <div className={css(styles.outputSchemaToggle)}>
      <FormGroup className={classNames('u-no-spacing-bottom', css(styles.descriptionContents))}>
        <div className="settingsPageContainer-label">
          <Button
            buttonStyle={ButtonStyle.LINK}
            onClick={props.onClick}
            className={props.hasOutputSchema ? css(styles.removeText) : ''}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaEditor',
              element: 'outputSchemaControlButton',
              key: props.hasOutputSchema ? 'remove' : 'add',
            }}
          >
            {props.hasOutputSchema ? 'Remove output schema' : 'Add output schema'}
          </Button>
        </div>
        <div className={css(styles.descriptionText)}>
          {`Output schema cannot be ${props.hasOutputSchema ? 'removed' : 'added'} after the task schema is
          created.`}
        </div>
      </FormGroup>
    </div>
  );
}

export function ProcessSchemaErrorList(props: ProcessSchemaErrorListProps): JSX.Element {
  return <ErrorList errorMessage={props.error} />;
}

export function verifySchemaFields(fields: Array<ClientSchemaField>, schemaId?: string | null): boolean {
  if (schemaId) {
    const schemaType = APIUtil.apiIdToMaybeType(schemaId);
    if (
      schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ||
      schemaType === ModelTypeConstants.PROCESS_OUTPUT_SCHEMA
    ) {
      const maxUnarchivedFieldsLimit = BenchlingConfig.WORKFLOWS_MAX_UNARCHIVED_FIELDS_PER_SCHEMA;
      if (maxUnarchivedFieldsLimit !== -1) {
        const unarchivedFieldCount = fields.filter((field) => field.archiveRecord == null).length;
        if (unarchivedFieldCount > maxUnarchivedFieldsLimit) {
          notifications.error(
            `You may not add more than ${maxUnarchivedFieldsLimit} unarchived fields on a ${schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ? 'task' : 'output'} schema.`
          );
          return false;
        }
      }

      const maxSnapshotFieldsLimit = BenchlingConfig.WORKFLOWS_MAX_SNAPSHOT_FIELDS_PER_SCHEMA;
      if (maxSnapshotFieldsLimit !== -1) {
        const snapshotFieldCount = fields.filter((field) => field.isSnapshot).length;
        if (snapshotFieldCount > maxSnapshotFieldsLimit) {
          notifications.error(
            `You may not add more than ${maxSnapshotFieldsLimit} snapshot computed fields on a ${schemaType === ModelTypeConstants.PROCESS_TASK_SCHEMA ? 'task' : 'output'} schema.`
          );
          return false;
        }
      }
    }
  }
  return true;
}

export function ProcessSchemaFieldTable(props: ProcessSchemaFieldTableProps): JSX.Element {
  if (!(props.storableSchemas && props.resultSchemas)) {
    return (
      <div className={props.hasBottomLink ? css(styles.fieldTableWithLink) : css(styles.fieldTable)}>
        <FieldTableLoading schemaType={props.schemaType} />
      </div>
    );
  }

  const storableSchemas = filterSchemasByRegistryOrganization(props.storableSchemas, props.ownerId);
  const resultSchemas = filterSchemasByOrganizationJson(props.resultSchemas, props.ownerId);

  return (
    <div className={props.hasBottomLink ? css(styles.fieldTableWithLink) : css(styles.fieldTable)}>
      <FieldTable
        schemaId={props.schema?.id ?? null}
        schemaType={props.schemaType}
        isBeingCreated={props.schema == null}
        onFieldsChange={props.onFieldsChange}
        ownerUrlId={props.ownerUrlId}
        ownerId={props.ownerId}
        registryId={props.registryId}
        itemClass={ProcessSchemaFieldTableRow}
        fields={props.fields}
        storableSchemas={graphQLArrayToREST('StorableSchema', storableSchemas)}
        assaySchemas={graphQLArrayToREST('ResultSchema', resultSchemas)}
        isDisabled={Boolean(props.isDisabled)}
        shouldShowIsRequired={true}
        shouldShowIsIntegration={false}
        shouldShowIsParentLink={false}
        savingState={SavingState.COMPLETED}
        schema={props.schema}
        dataTestAttrs={props.dataTestAttrs}
        submissionWarnings={props.changeWarnings}
        archiveWarnings={props.changeWarnings}
        verifyFields={verifySchemaFields}
        fieldNameRenderingProps={props.fieldNameRenderingProps}
        compactMode={props.compactMode}
      />
    </div>
  );
}

const SectionNumber = ({sectionNumber}: {sectionNumber: number}): JSX.Element => (
  <div className={css(styles.schemaEditorSectionNumber)}>{sectionNumber}</div>
);

function ProcessTaskSchemaEditorSection(props: ProcessTaskSchemaEditorSectionProps): JSX.Element {
  return (
    <div className={css(styles.schemaEditorSection)}>
      {!props.shouldHideSectionHeader && (
        <div className={css(styles.schemaEditorSectionHeader)}>
          <SectionNumber sectionNumber={props.sectionNumber} />
          <div className="legacy-font-proxima-14">{props.sectionTitle}</div>
          {props.actionButton && (
            <Tooltip text={props.actionButton.tooltipTitle}>
              <div
                className={classNames(
                  'faded u-small-padding-left u-small-padding-right pull-right u-primary-pointer'
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  props.actionButton?.onClick();
                }}
              >
                <BenchlingIcon icon={props.actionButton.icon} />
              </div>
            </Tooltip>
          )}
        </div>
      )}
      <div
        className={css(
          !props.noSectionSpacing && styles.schemaEditorSectionChildrenSpacing,
          !props.isLastSection && !props.shouldHideSectionHeader && styles.schemaEditorSectionChildrenBorder
        )}
      >
        {props.children}
      </div>
    </div>
  );
}

const LoadingProcessTaskSchemaEditor = (): JSX.Element => (
  <ProcessTaskSchemaEditorContainer subtitle="">
    <HorizontalThrobber />
  </ProcessTaskSchemaEditorContainer>
);

function InnerProcessTaskSchemaEditor(props: {
  schemaId: string | null;
  processTaskSchema: ProcessTaskSchema | null;
  refetchProcessSchema?: () => void;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest) => Promise<void>;
  isSettingsPage?: boolean;
  registryId?: string;
  stickyCreateButton?: boolean;
}): JSX.Element {
  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  const boxSchemasResult = useBoxSchemasQueryWithRegistry();
  const containerSchemasResult = useContainerSchemasQueryWithRegistry();
  const locationSchemasResult = useLocationSchemasQueryWithRegistry();
  const plateSchemasResult = usePlateSchemasQueryWithRegistry();
  const resultSchemasResult = useAllResultSchemasQuery();
  const processTaskSchemasResult = useBenchlingQuery<AllProcessTaskSchemasForSchemaConfigQuery>(
    ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY
  );
  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);

  const flowchartConfigVersion = props.processTaskSchema?.flowchartConfig?.currentFlowchartConfigVersion;

  // This calculation should guarantee that downstream components will never bump into an inaccessible schema.
  const isAnyPartOfFlowchartConfigInaccessible: boolean =
    flowchartConfigVersion != null &&
    !isInaccessible(flowchartConfigVersion) &&
    flowchartConfigVersion.availableSchemas.some((schema) => isInaccessible(schema));

  let innerProps: InnerProcessTaskSchemaEditorProps;

  if (
    tagSchemasResult.type === QueryResultType.SUCCESS &&
    boxSchemasResult.type === QueryResultType.SUCCESS &&
    containerSchemasResult.type === QueryResultType.SUCCESS &&
    locationSchemasResult.type === QueryResultType.SUCCESS &&
    plateSchemasResult.type === QueryResultType.SUCCESS &&
    resultSchemasResult.type === QueryResultType.SUCCESS &&
    processTaskSchemasResult.type === QueryResultType.SUCCESS &&
    statusLifecyclesResult.type === QueryResultType.SUCCESS
  ) {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: [
        ...boxSchemasResult.data.boxSchemas,
        ...containerSchemasResult.data.containerSchemas,
        ...locationSchemasResult.data.locationSchemas,
        ...plateSchemasResult.data.plateSchemas,
      ],
      tagSchemas: tagSchemasResult.data.tagSchemas,
      taskSchemas:
        // Ensure that the "supported" task schemas list always contains the latest update to the task schema
        // being edited.
        props.processTaskSchema != null
          ? insertOrReplaceTaskSchema(
              processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
              props.processTaskSchema
            )
          : processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
      resultSchemas: resultSchemasResult.data.resultSchemas,
      statusLifecycles: statusLifecyclesResult.data.processTaskStatusLifecycles,
      isAnyPartOfFlowchartConfigInaccessible,
      registryId: props.registryId,
    };
  } else {
    innerProps = {
      schemaId: props.schemaId,
      storableSchemas: null,
      tagSchemas: null,
      taskSchemas: null,
      resultSchemas: null,
      statusLifecycles: null,
      isAnyPartOfFlowchartConfigInaccessible: null,
      registryId: props.registryId,
    };
  }

  return (
    <ProcessTaskSchemaEditorContent
      processTaskSchema={props.processTaskSchema}
      refetchProcessSchema={props.refetchProcessSchema}
      restrictedToExecutionType={props.restrictedToExecutionType}
      restrictedToOrganizationId={props.restrictedToOrganizationId}
      createProcessTaskSchema={props.createProcessTaskSchema}
      isSettingsPage={props.isSettingsPage}
      stickyCreateButton={props.stickyCreateButton}
      {...innerProps}
    />
  );
}

export function NonConfigurableSchema(): JSX.Element {
  return <div>This task schema is not user-configurable.</div>;
}

function shouldRenderTabbedContainer(): boolean {
  return (
    isWorkflowCreateTaskFormsEnabled() ||
    BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_UI_PROCESS_TASK_SCHEMAS ||
    BenchlingConfig.REQUESTS_V2_ENABLE
  );
}

export const MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query MinimalProcessTaskSchemaEditorQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        flowchartConfig {
          id
        }
        relevantForms {
          ... on FormDefinitionNode {
            id
            isValid
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    # Registries are needed to map organization IDs to registry IDs for field tables
    # this lets us link to the field's definition
    registries {
      ... on RegistryNode {
        id
        organization {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcessTaskSchemaEditor(props: ProcessTaskSchemaEditorProps): JSX.Element {
  // We intentionally close the sidepanel when opening a task schema editor
  useOnMount(() => {
    if (
      (isCreatingNewTaskSchema(props) && Boolean(props.isSettingsPage ?? true)) ||
      isEditingExistingTaskSchema(props)
    ) {
      SidepanelActions.collapse();
    }
  });

  if (props.schemaId == null) {
    const isSettingsPage = props.isSettingsPage ?? true;
    const innerEditor = (
      <InnerProcessTaskSchemaEditor
        schemaId={null}
        processTaskSchema={null}
        restrictedToExecutionType={props.restrictedToExecutionType}
        restrictedToOrganizationId={props.restrictedToOrganizationId}
        createProcessTaskSchema={props.createProcessTaskSchema}
        isSettingsPage={props.isSettingsPage}
        stickyCreateButton={props.stickyCreateButton}
        registryId={undefined}
      />
    );
    if (!isSettingsPage) {
      return innerEditor;
    }
    return <ProcessTaskSchemaEditorContainer subtitle={null}>{innerEditor}</ProcessTaskSchemaEditorContainer>;
  }

  return (
    <BenchlingQuery<MinimalProcessTaskSchemaEditorQuery, MinimalProcessTaskSchemaEditorQueryVariables>
      query={MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY}
      renderLoading={LoadingProcessTaskSchemaEditor}
      variables={{schemaId: props.schemaId}}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) =>
        (() => {
          if (isInaccessible(data.processTaskSchema)) {
            return (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={data.processTaskSchema}
                modelType="processTaskSchema"
              />
            );
          }

          if (!canConfigureProcessTaskSchema(data.processTaskSchema)) {
            return <NonConfigurableSchema />;
          }

          const ownerToRegistryMap = createOwnerToRegistryMap(data.registries);
          const schemaOwner = data.processTaskSchema.organization;
          const registryId = getRegistryIdForOwner(schemaOwner.id, ownerToRegistryMap);

          const showFlowchartOnlyEditor =
            BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1 &&
            isFlowchartType(data.processTaskSchema.executionType) &&
            data.processTaskSchema.flowchartConfig != null;

          if (showFlowchartOnlyEditor) {
            ensureDefaultProcessesPluginRegistered();
          }
          const innerEditor = showFlowchartOnlyEditor ? (
            // In the flowchart first config paradigm, the flowchart config editor is the main entrypoint for
            // editing flowcharts.
            // Users can edit fields in the root task or output schema by interacting with the root and output
            // nodes directly.
            // TODO(BNCH-133942): Migrate inaccessible and archived node schemas in the new editor.
            <ProcessFlowchartConfigEditor
              flowchartConfigId={data.processTaskSchema.flowchartConfig!.id}
              flowchartTypeName={DefaultProcessesPlugin.FLOWCHART_TYPE_NAME}
              locked={!data.processTaskSchema.permissions.writable}
              ownerToRegistryMap={ownerToRegistryMap}
            />
          ) : (
            // Using the pre-flowchart-first-config fragments when querying for the flowchart config results
            // in backend crashes when loading schemas which rely on task definition fields. So here we
            // explicitly query for the flowchart config using the old fragments only if we're using the old
            // editor.
            <BenchlingQuery<ProcessTaskSchemaEditorQuery, ProcessTaskSchemaEditorQueryVariables>
              query={PROCESS_TASK_SCHEMA_EDITOR_QUERY}
              renderLoading={LoadingProcessTaskSchemaEditor}
              variables={{schemaId: props.schemaId}}
              skip={data.processTaskSchema.flowchartConfig == null}
              fetchPolicy="cache-and-network"
            >
              {(innerData, result) => {
                const fullTaskSchema = {
                  ...assertIsAccessible(data.processTaskSchema),
                  flowchartConfig:
                    result.type === QueryResultType.SUCCESS
                      ? assertIsAccessible(innerData.processTaskSchema).flowchartConfig
                      : null,
                };
                return (
                  <InnerProcessTaskSchemaEditor
                    schemaId={props.schemaId}
                    processTaskSchema={fullTaskSchema}
                    refetchProcessSchema={refetch}
                    registryId={registryId}
                  />
                );
              }}
            </BenchlingQuery>
          );

          if (showFlowchartOnlyEditor) {
            // The flowchart-only editor already renders its own container.
            // Do not wrap it in SettingsPageContainer.
            return innerEditor;
          }

          if (shouldRenderTabbedContainer()) {
            return (
              <SettingsPageContainer
                {...makeProcessTaskSchemaSettingsPageContainerProps({
                  subtitle: data.processTaskSchema.name,
                  processTaskSchema: data.processTaskSchema,
                })}
              >
                <ProcessTaskSchemaEditorTabbedContainer
                  subtitle={data.processTaskSchema.name}
                  processTaskSchema={data.processTaskSchema}
                  configurationTab={innerEditor}
                  schemaId={props.schemaId}
                />
              </SettingsPageContainer>
            );
          }
          return (
            <ProcessTaskSchemaEditorContainer
              subtitle={data.processTaskSchema.name}
              processTaskSchema={data.processTaskSchema}
            >
              {innerEditor}
            </ProcessTaskSchemaEditorContainer>
          );
        })()
      }
    </BenchlingQuery>
  );
}

export const styles = StyleSheet.create({
  schemaEditorSection: {
    display: 'block',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  schemaEditorSectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  schemaEditorSectionNumber: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: '12px',
    width: '20px',
    height: '20px',
    lineHeight: '20px',
    justifyContent: 'center',
    backgroundColor: Colors.WHITE,
    color: Colors.GRAY_DARKER,
    border: `1px solid ${Colors.GRAY_DARKER}`,
    borderRadius: '50%',
    boxShadow: `0 0 0 4px ${Colors.WHITE}`,
    marginRight: '12px',
  },
  schemaEditorSectionChildrenSpacing: {
    position: 'relative',
    marginLeft: '10px',
    paddingLeft: '22px',
    paddingTop: '8px',
    marginTop: '8px',
    paddingBottom: '8px',
    marginBottom: '8px',
  },
  schemaEditorSectionChildrenBorder: {
    borderLeft: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  schemaErrorMessage: {
    position: 'relative',
    margin: '8px 10px',
    padding: '8px 22px',
  },
  ownerField: {
    flex: '1',
  },
  fieldTable: {
    marginBottom: '32px',
  },
  fieldTableWithLink: {
    marginBottom: '18px',
  },
  outputSchemaToggle: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '32px',
  },
  createButton: {
    marginLeft: '32px',
  },
  stickyCreateButtonFooter: {
    position: 'sticky',
    bottom: -16,
    backgroundColor: Colors.WHITE,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    padding: '16px 24px',
    margin: '0 -16px -16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    zIndex: 1,
  },
  stickyCreateButtonFooterMessage: {
    flex: 1,
    color: Colors.GRAY_DARKER,
    fontSize: '13px',
    lineHeight: '16px',
  },
  descriptionContents: {
    flex: 0.5,
  },
  descriptionText: {
    color: Colors.GRAY_DARK,
    fontSize: '13px',
    lineHeight: '16px',
  },
  flowchartText: {
    display: 'inline-block',
    marginBottom: '12px',
  },
  removeText: {
    color: Colors.RED_LIGHT,
  },
  taskGroupInstructionEditorContainer: {
    marginBottom: '24px',
    fontSize: '12px',
  },
  cloneButton: {
    margin: 8,
  },
  configurationWarningBanner: {
    marginBottom: 16,
  },
});
